import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { gapi } from 'gapi-script';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';

declare const google: any;

@Component({
  selector: 'auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
})
export class AuthComponent implements OnInit, OnDestroy {
  buttonText: string = '';
  buttonPath: string = '/auth';
  email: string | undefined;
  showGoogleLogin = true;
  private clientId: string = environment?.clientId;
  private auth2: any;
  private routerSub?: Subscription;

  constructor(
    private router: Router,
    private authService: AuthService,
    private toaster: ToastService,
    private ngZone: NgZone // ✅ Needed for UI refresh after Google login
  ) {}

  ngOnInit() {
    this.updateGoogleLoginVisibility(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.updateGoogleLoginVisibility(event.urlAfterRedirects));

    // Initialize Google Auth with forced account chooser
    gapi.load('auth2', () => {
      this.auth2 = gapi.auth2.init({
        client_id: this.clientId,
        cookiepolicy: 'single_host_origin',
        prompt: 'select_account', // ✅ Always show account picker
        scope: 'profile email'
      });
    });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  private updateGoogleLoginVisibility(url: string): void {
    const isDesignPartnerAuth =
      url.includes('/auth/register/design-partner') ||
      url.includes('/auth/login/design-partner');
    this.showGoogleLogin = !isDesignPartnerAuth;
  }

  loginWithGoogle() {
    if (!this.auth2) {
      console.warn('Google Auth not initialized yet.');
      return;
    }

    this.auth2.signIn()
      .then((user: any) => {
        // Run inside Angular zone so UI updates instantly
        this.ngZone.run(() => {
          const profile = user.getBasicProfile();
          const authResponse = user.getAuthResponse();
          const google_token = authResponse.id_token;
          const name = profile.getName();
          const email = profile.getEmail();

          const userData = { name, google_token, email };

          this.authService.googleSignin(userData).subscribe({
            next: (response) => {
              const token = response.token;
              const currentUser = response.user;
              this.authService.setAuthData(token, currentUser);

              // Show success toast
              this.toaster.showToast('Login Successful', 'success');

              // Navigate to home
              this.router.navigate(['/'], { replaceUrl: true });
            },
            error: (error) => {
              console.error('Login failed:', error);
              this.toaster.showToast('Login failed', 'error');
            },
          });
        });
      })
      .catch((error: any) => {
        this.ngZone.run(() => {
          console.error('Error during Google Sign-In:', error);
          this.toaster.showToast('Google Sign-In failed', 'error');
        });
      });
  }
}
